import classNames from "classnames";
import React, { PropsWithChildren, ReactNode } from "react";
import { LinkInfo } from "../../models/shared/LinkInfo";
import { replaceWithUnicode } from "../../shared/string";
import SanityLink from "../SanityLink";

export enum ButtonSize {
  Normal = "normal-100",
  NormalWide = "normal-100-w",
  Small = "small-100",
  SmallWide = "small-100-w",
  Small50 = "small-50",
  Small30 = "small-30"
}

// available color options from button.css
export type ButtonColor =
  | "primary-alt"
  | "primary-dim"
  | "primary-inverted"
  | "primary-light"
  | "primary-transparent"
  | "primary"
  | "secondary";

type BaseProps = {
  applyDisabledClasses?: boolean;
  children?: ReactNode;
  className?: string;
  color: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClickLink?: (url: string | null | undefined) => void;
  size: ButtonSize;
  style?: null | "border";
};

export type ButtonProps = BaseProps &
  (
    | ({
        type?: "link";
      } & LinkInfo)
    | {
        type?: "button" | "submit" | "reset";
      }
  );

const Button = ({
  applyDisabledClasses = true,
  children,
  className = "",
  color,
  disabled,
  onClick,
  onClickLink,
  size,
  style,
  type,
  ...restOfProps
}: PropsWithChildren<ButtonProps>) => {
  let focusShadowOutlineColorClassName: string | undefined;

  switch (color) {
    case "primary":
      focusShadowOutlineColorClassName = "focus:shadow-outline-primary";
      break;
    case "primary-light":
      focusShadowOutlineColorClassName = "focus:shadow-outline-primary-light";
      break;
    case "primary-inverted":
      focusShadowOutlineColorClassName = "focus:shadow-outline-primary-inverted";
      break;
    case "primary-transparent":
      focusShadowOutlineColorClassName = "focus:shadow-outline-primary-transparent";
      break;
    case "secondary":
      focusShadowOutlineColorClassName = "focus:shadow-outline-secondary";
      break;
    case "neutral":
      focusShadowOutlineColorClassName = "focus:shadow-outline-neutral";
      break;
    case "error":
      focusShadowOutlineColorClassName = "focus:shadow-outline-error";
      break;
  }

  const buttonClassNames = classNames(
    className,
    "anticimex-button",
    `anticimex-button-size--${size}`,
    `anticimex-button-color--${color}`,
    `hover:anticimex-button-highlight-color--${color}`,
    `focus:anticimex-button-highlight-color--${color}`,
    "focus:outline-none",
    focusShadowOutlineColorClassName,
    {
      [`disabled:anticimex-button-disabled disabled:anticimex-button-disabled-color--${color}`]:
        applyDisabledClasses,
      "disabled:cursor-not-allowed": !applyDisabledClasses,
      [`anticimex-button-style--${style}`]: !!style?.length
    }
  );

  const renderButtonType = () => {
    switch (type) {
      case "button":
      case "submit":
      case "reset":
      default: {
        return (
          <button
            className={classNames(buttonClassNames, "cursor-pointer")}
            disabled={disabled}
            onClick={!disabled ? onClick : undefined}
            type={type ?? "button"}
          >
            {typeof children === "string" ? replaceWithUnicode(children) : children}
          </button>
        );
      }
      case "link": {
        return (
          <SanityLink {...restOfProps} className={buttonClassNames} onClick={onClickLink}>
            {children}
          </SanityLink>
        );
      }
    }
  };

  return renderButtonType();
};

export default Button;
